import { useEffect, useRef, useState } from "react";
import { devWarn } from "../lib/devLog";
import { MapPin, LocateFixed, Loader2 } from "lucide-react";
import { loadGoogleMaps, MOROCCO_CENTER } from "../lib/maps";
import { t } from "../i18n";

/**
 * Interactive Google map with a draggable, bouncing rose pin, plus Morocco-only
 * address autocomplete. The key comes from /api/bootstrap (referrer-restricted).
 *
 * Props:
 *  - apiKey            Google Maps JS key
 *  - value             { lat, lng } | null
 *  - center            { lat, lng } | null   city centre to open on
 *  - autocomplete      boolean               enable Places autocomplete input
 *  - onChange({ lat, lng, address })
 *  - testid
 */
export function DeliveryMap({ apiKey, value, center, autocomplete = true, onChange, testid = "delivery-map", locale = "en" }) {
  const mapEl = useRef(null);
  const searchEl = useRef(null);
  const suggestEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const acRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [locating, setLocating] = useState(false);
  const cb = useRef(onChange);
  cb.current = onChange;

  // Hide the map entirely if Google reports an invalid/misconfigured key.
  useEffect(() => {
    if (window.__gmapsAuthFailed) setStatus("error");
    const onFail = () => setStatus("error");
    window.addEventListener("gmaps-auth-failed", onFail);
    return () => window.removeEventListener("gmaps-auth-failed", onFail);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then(async (google) => {
        if (cancelled || !mapEl.current) return;
        const start = value || center || MOROCCO_CENTER;
        const zoom = value ? 16 : center ? 12 : 6;
        const map = new google.maps.Map(mapEl.current, {
          center: start,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: "greedy",
        });
        mapRef.current = map;

        const marker = new google.maps.Marker({
          position: value || center || MOROCCO_CENTER,
          map,
          draggable: true,
          visible: !!value,
          animation: google.maps.Animation.BOUNCE,
          icon: {
            url: "/rose-pin.png",
            scaledSize: new google.maps.Size(46, 46),
            anchor: new google.maps.Point(23, 44),
          },
        });
        markerRef.current = marker;

        const place = (pos) => {
          marker.setPosition(pos);
          marker.setVisible(true);
          marker.setAnimation(google.maps.Animation.BOUNCE);
          cb.current?.({ lat: pos.lat, lng: pos.lng });
        };

        map.addListener("click", (e) => place({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
        marker.addListener("dragend", (e) =>
          cb.current?.({ lat: e.latLng.lat(), lng: e.latLng.lng() })
        );

        if (autocomplete && searchEl.current) {
          // Current Places API (AutocompleteSuggestion) — the legacy Autocomplete widget is retired.
          const places = await google.maps.importLibrary("places");
          acRef.current = { token: null };
          const render = (predictions) => {
            const list = suggestEl.current;
            if (!list) return;
            list.innerHTML = "";
            list.style.display = predictions.length ? "block" : "none";
            predictions.forEach((prediction) => {
              const btn = document.createElement("button");
              btn.type = "button";
              btn.className = "w-full text-start px-3 py-2 text-sm text-ink hover:bg-petal/30";
              btn.textContent = prediction.text?.text || "";
              btn.onclick = async () => {
                list.style.display = "none";
                searchEl.current.value = prediction.text?.text || "";
                acRef.current.token = null;
                const p = prediction.toPlace();
                await p.fetchFields({ fields: ["location", "formattedAddress"] });
                if (!p.location) return;
                const pos = { lat: p.location.lat(), lng: p.location.lng() };
                map.panTo(pos);
                map.setZoom(16);
                marker.setPosition(pos);
                marker.setVisible(true);
                marker.setAnimation(google.maps.Animation.BOUNCE);
                cb.current?.({ ...pos, address: p.formattedAddress || "" });
              };
              list.appendChild(btn);
            });
          };
          let timer = null;
          searchEl.current.addEventListener("input", (e) => {
            const input = e.target.value;
            clearTimeout(timer);
            if (input.trim().length < 3) { render([]); return; }
            timer = setTimeout(async () => {
              if (!acRef.current.token) acRef.current.token = new places.AutocompleteSessionToken();
              try {
                const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                  input, sessionToken: acRef.current.token, includedRegionCodes: ["ma"],
                  locationBias: center?.lat ? { center: { lat: center.lat, lng: center.lng }, radius: 20000 } : undefined,
                });
                render((suggestions || []).slice(0, 5).map((s) => s.placePrediction).filter(Boolean));
              } catch (_) {
                render([]);
              }
            }, 250);
          });
        }

        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // keep the pin in sync when the parent sets a value externally
  useEffect(() => {
    if (status !== "ready" || !value || !markerRef.current || !mapRef.current) return;
    markerRef.current.setPosition(value);
    markerRef.current.setVisible(true);
    mapRef.current.panTo(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng, status]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(false);
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        if (mapRef.current) {
          mapRef.current.panTo(pos);
          mapRef.current.setZoom(16);
        }
        if (markerRef.current) {
          markerRef.current.setPosition(pos);
          markerRef.current.setVisible(true);
        }
        cb.current?.(pos);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  if (status === "error") return null;

  return (
    <div className="space-y-2" data-testid={testid}>
      {autocomplete ? (
        <div className="relative">
          <MapPin size={15} className="absolute start-3 top-1/2 -translate-y-1/2 text-brass pointer-events-none" />
          <input
            ref={searchEl}
            data-testid={`${testid}-search`}
            className="field ps-9"
            placeholder={t(locale, "mapSearchPlaceholder")}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          />
          <div ref={suggestEl} data-testid={`${testid}-suggestions`} style={{ display: "none" }}
               className="absolute z-30 inset-x-0 top-full mt-1 bg-plaster border border-brass-light shadow-lg max-h-56 overflow-y-auto" />
        </div>
      ) : null}
      <div className="relative rounded-sm overflow-hidden border border-brass/30" style={{ height: 260 }}>
        <div ref={mapEl} className="w-full h-full" />
        {status === "loading" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-plaster/70">
            <Loader2 className="animate-spin text-brass" size={22} />
          </div>
        ) : null}
        <button
          type="button"
          data-testid={`${testid}-locate`}
          onClick={useMyLocation}
          className="absolute bottom-3 end-3 bg-ink text-plaster text-xs px-3 py-2 rounded-sm flex items-center gap-1.5 shadow hover:bg-ink/90 transition-colors"
        >
          {locating ? <Loader2 size={13} className="animate-spin" /> : <LocateFixed size={13} />}
          {t(locale, "myPosition")}
        </button>
      </div>
    </div>
  );
}

export default DeliveryMap;

/**
 * Read-only live map: a moving driver marker heading toward the delivery rose pin.
 * Renders nothing without a Google Maps key (graceful).
 */
export function LiveMap({ apiKey, driver, destination, testid = "live-map" }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const driverRef = useRef(null);
  const destRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!apiKey) { setStatus("nokey"); return; }
    let cancelled = false;
    loadGoogleMaps(apiKey).then((google) => {
      if (cancelled || !el.current) return;
      const center = driver || destination || MOROCCO_CENTER;
      const map = new google.maps.Map(el.current, {
        center, zoom: 14, mapTypeControl: false, streetViewControl: false, fullscreenControl: false, clickableIcons: false,
      });
      mapRef.current = map;
      if (destination) {
        destRef.current = new google.maps.Marker({
          position: destination, map,
          icon: { url: "/rose-pin.png", scaledSize: new google.maps.Size(44, 44), anchor: new google.maps.Point(22, 42) },
        });
      }
      setStatus("ready");
    }).catch(() => !cancelled && setStatus("error"));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  useEffect(() => {
    if (status !== "ready" || !driver || !window.google || !mapRef.current) return;
    if (!driverRef.current) {
      driverRef.current = new window.google.maps.Marker({
        position: driver, map: mapRef.current,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#14322A", fillOpacity: 1,
                strokeColor: "#fff", strokeWeight: 3 },
        title: "Livreur",
      });
    } else {
      driverRef.current.setPosition(driver);
    }
    const b = new window.google.maps.LatLngBounds();
    b.extend(driver);
    if (destination) b.extend(destination);
    mapRef.current.fitBounds(b, 70);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.lat, driver?.lng, status]);

  if (status === "nokey" || status === "error") return null;
  return (
    <div className="relative rounded-sm overflow-hidden border border-brass/30" style={{ height: 300 }} data-testid={testid}>
      <div ref={el} className="w-full h-full" />
      {status === "loading" ? <div className="absolute inset-0 flex items-center justify-center bg-plaster/70"><Loader2 className="animate-spin text-brass" /></div> : null}
    </div>
  );
}

/** Admin dispatch map: green dots for active drivers, rose pins for open deliveries. */
export function DispatchMap({ apiKey, drivers = [], deliveries = [], testid = "dispatch-map" }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef([]);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);  // bump to retry a failed load

  // Google fires gm_authFailure (surfaced as this event) when the key is
  // rejected for the current domain — the JS SDK loads but tiles never paint.
  useEffect(() => {
    if (window.__gmapsAuthFailed) setStatus("authfail");
    const onFail = () => setStatus("authfail");
    window.addEventListener("gmaps-auth-failed", onFail);
    return () => window.removeEventListener("gmaps-auth-failed", onFail);
  }, []);

  useEffect(() => {
    if (!apiKey) { setStatus("nokey"); return; }
    if (window.__gmapsAuthFailed) { setStatus("authfail"); return; }
    setStatus("loading");
    let cancelled = false;
    loadGoogleMaps(apiKey).then((google) => {
      if (cancelled || !el.current) return;
      try {
        mapRef.current = new google.maps.Map(el.current, {
          center: MOROCCO_CENTER, zoom: 6, mapTypeControl: false, streetViewControl: false, fullscreenControl: false, clickableIcons: false,
        });
        setStatus(window.__gmapsAuthFailed ? "authfail" : "ready");
      } catch (e) {
        devWarn("[DispatchMap] init failed", e);
        setStatus("error");
      }
    }).catch((e) => {
      devWarn("[DispatchMap] load failed", e);
      if (!cancelled) setStatus("error");
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, attempt]);

  useEffect(() => {
    if (status !== "ready" || !window.google?.maps || !mapRef.current) return;
    const g = window.google.maps;
    markers.current.forEach((m) => m.setMap(null));
    markers.current = [];
    // Only accept rows whose coordinates are real finite numbers — a stray
    // undefined/null from the API throws `InvalidValueError` inside
    // `new google.maps.Marker(...)` and unmounts the admin shell.
    const validPos = (v) => Number.isFinite(v?.lat) && Number.isFinite(v?.lng);
    try {
      const b = new g.LatLngBounds();
      deliveries.forEach((o) => {
        if (!validPos(o)) return;
        const m = new g.Marker({ position: { lat: o.lat, lng: o.lng }, map: mapRef.current, title: `${o.order_number} · ${o.recipient_name}`,
          icon: { url: "/rose-pin.png", scaledSize: new g.Size(34, 34), anchor: new g.Point(17, 32) } });
        markers.current.push(m); b.extend({ lat: o.lat, lng: o.lng });
      });
      drivers.forEach((d) => {
        if (!validPos(d)) return;
        const m = new g.Marker({ position: { lat: d.lat, lng: d.lng }, map: mapRef.current, title: d.name, zIndex: 999,
          icon: { path: g.SymbolPath.CIRCLE, scale: 8, fillColor: d.driver_type === "network" ? "#14322A" : "#B4884A", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 } });
        markers.current.push(m); b.extend({ lat: d.lat, lng: d.lng });
      });
      if (!b.isEmpty()) mapRef.current.fitBounds(b, 60);
    } catch (e) {
      devWarn("[DispatchMap] marker paint failed", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, drivers, deliveries]);

  if (status === "authfail") {
    return (
      <div className="card p-4 text-sm text-muted space-y-2" data-testid="dispatch-map-authfail">
        <p className="text-ink font-medium">Carte bloquée par Google sur ce domaine.</p>
        <p>
          La clé Google Maps existe mais <b>{typeof window !== "undefined" ? window.location.hostname : ""}</b> n'est
          pas autorisé. Dans Google Cloud Console → APIs &amp; Services → Identifiants → votre clé, ajoutez ce domaine aux
          « référents HTTP autorisés » (ex. <code className="bg-plaster-deep px-1">{`https://${window.location.hostname}/*`}</code>),
          vérifiez que « Maps JavaScript API » est activée et que la facturation est active. Les changements prennent
          quelques minutes.
        </p>
        <p className="text-xs">La liste ci-dessous reste à jour en attendant.</p>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="card p-4 text-sm text-muted space-y-2" data-testid="dispatch-map-error">
        <p>Carte indisponible pour le moment (clé Google Maps rejetée ou domaine non autorisé). Liste ci-dessous.</p>
        <button type="button" data-testid="dispatch-map-retry" onClick={() => setAttempt((a) => a + 1)}
                className="bg-ink text-plaster text-xs px-3 py-1.5 rounded-sm hover:bg-ink/90 transition-colors">
          Réessayer
        </button>
      </div>
    );
  }
  if (status === "nokey") return null;
  return (
    <div className="relative rounded-sm overflow-hidden border border-brass/30" style={{ height: 380 }} data-testid={testid}>
      <div ref={el} className="w-full h-full" />
      {status === "loading" ? <div className="absolute inset-0 flex items-center justify-center bg-plaster/70"><Loader2 className="animate-spin text-brass" /></div> : null}
    </div>
  );
}
